/**
 * BookingChoiceModal — surfaces our in-house booking as the primary
 * path, with the venue's external partner (Sevenrooms / OpenTable /
 * Quandoo etc.) embedded as a secondary fallback INSIDE the modal so
 * users never leave hustleeats.
 *
 * Why a modal vs. a hard redirect?
 *   1. Our booking system is materially better than partner systems
 *      (allergen-aware Maître'D AI, multi-venue Cabravale "Sister
 *      venue" hopping, loyalty integration, table-side dietary
 *      grey-out). The modal puts our flow FIRST.
 *   2. Embedding the partner system in an iframe (rather than
 *      window.open) keeps the user on-domain — click → complete
 *      conversion can be tracked via `/api/booking/track`.
 *   3. Lets us A/B copy: "Book direct" vs. "Use Sevenrooms" lets
 *      us measure how many guests pick the better path.
 *
 * Props:
 *   slug              — restaurant slug (drives our internal /reserve link)
 *   externalUrl       — partner URL (e.g. Sevenrooms)
 *   provider          — "sevenrooms" | "opentable" | "quandoo" | ...
 *   open / onClose
 *   venueName         — for the heading
 *   preferInhouse     — when true, our CTA is bold + first; the
 *                       partner CTA is a softer secondary link.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ExternalLink, ShieldCheck,
              CalendarClock, Loader2 } from "lucide-react";

const PROVIDER_LABEL = {
  sevenrooms:        "Sevenrooms",
  opentable:         "OpenTable",
  quandoo:           "Quandoo",
  resy:              "Resy",
  square_appointments: "Square",
  fresha:            "Fresha",
  timely:            "Timely",
  calendly:          "Calendly",
};


function trackClick(slug, choice, extra = {}) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // Fire-and-forget; never block the UI on the metric write.
  try {
    fetch(`${base}/api/booking/track`, {
      method:      "POST",
      headers:     { "Content-Type": "application/json" },
      credentials: "include",
      body:        JSON.stringify({
        slug, choice, ...extra,
        ts: new Date().toISOString(),
      }),
      keepalive:   true,
    }).catch(() => {});
  } catch { /* noop */ }
}


export default function BookingChoiceModal({
  slug, externalUrl, provider, open, onClose,
  venueName, preferInhouse = true,
}) {
  const [tab, setTab] = useState("inhouse");   // "inhouse" | "partner"
  const [iframeLoading, setIframeLoading] = useState(true);
  const partnerLabel = PROVIDER_LABEL[provider] || "partner system";

  // Reset to in-house tab every time the modal re-opens
  useEffect(() => {
    if (open) {
      setTab("inhouse");
      setIframeLoading(true);
      trackClick(slug, "modal_open", { provider });
    }
  }, [open, slug, provider]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center
                          justify-center p-3 sm:p-6
                          bg-black/65 backdrop-blur-sm"
            data-testid="booking-choice-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>

      <div className="relative w-full max-w-2xl max-h-[90vh]
                             bg-stone-50 rounded-2xl shadow-2xl
                             overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200
                                flex items-center justify-between
                                bg-white">
          <div>
            <h2 className="font-serif text-xl text-stone-900"
                  data-testid="booking-modal-title">
              Reserve a table{venueName ? ` at ${venueName}` : ""}
            </h2>
            <p className="text-xs text-stone-500 mt-0.5">
              Choose how you&apos;d like to book.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close"
                    data-testid="booking-modal-close"
                    className="p-2 rounded-full hover:bg-stone-100
                                  text-stone-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 flex gap-2 border-b border-stone-200
                                bg-white">
          <TabBtn active={tab === "inhouse"}
                     onClick={() => setTab("inhouse")}
                     testid="booking-tab-inhouse">
            <Sparkles className="w-3.5 h-3.5" /> HustleEats &nbsp;
            <span className="bg-emerald-100 text-emerald-700
                                    text-[10px] px-1.5 py-0.5 rounded
                                    font-bold uppercase tracking-wider">
              Recommended
            </span>
          </TabBtn>
          {externalUrl && (
            <TabBtn active={tab === "partner"}
                       onClick={() => {
                         setTab("partner");
                         setIframeLoading(true);
                         trackClick(slug, "switch_to_partner",
                                      { provider });
                       }}
                       testid="booking-tab-partner">
              {partnerLabel}
            </TabBtn>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto bg-stone-50">
          {tab === "inhouse"
            ? <InHousePanel slug={slug}
                                 onClick={() =>
                                   trackClick(slug, "inhouse_click",
                                                { provider })} />
            : <PartnerPanel url={externalUrl}
                                 label={partnerLabel}
                                 loading={iframeLoading}
                                 onLoad={() => setIframeLoading(false)}
                                 onOpenExternal={() =>
                                   trackClick(slug, "partner_external",
                                                { provider })}/>}
        </div>
      </div>
    </div>
  );
}


function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm
                          font-medium border-b-2 -mb-px transition-colors
                          ${active
                            ? "border-stone-900 text-stone-900"
                            : "border-transparent text-stone-500 hover:text-stone-700"}`}>
      {children}
    </button>
  );
}


function InHousePanel({ slug, onClick }) {
  return (
    <div className="p-6 space-y-5" data-testid="booking-inhouse-panel">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-emerald-100 p-2 mt-0.5">
          <ShieldCheck className="w-4 h-4 text-emerald-700" />
        </div>
        <div>
          <p className="font-medium text-stone-900 mb-1">
            Why book direct?
          </p>
          <ul className="text-sm text-stone-600 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              Your saved allergens &amp; dietary preferences
              automatically reach the kitchen
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              Maître&apos;D AI answers questions in-app while you book
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              Earn loyalty points across all 6 Cabravale venues
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5">✓</span>
              No third-party fees — you talk straight to the venue
            </li>
          </ul>
        </div>
      </div>

      <Link to={`/r/${slug}/reserve`}
              onClick={onClick}
              data-testid="booking-inhouse-cta"
              className="block w-full text-center px-5 py-3
                            bg-stone-900 text-white rounded-full
                            font-bold hover:bg-stone-800
                            transition-colors">
        <CalendarClock className="inline w-4 h-4 mr-2 -mt-0.5" />
        Reserve through HustleEats
      </Link>
    </div>
  );
}


export function PartnerPanel({ url, label, loading, onLoad, onOpenExternal, fullHeight = false }) {
  return (
    <div className={fullHeight ? "relative w-full" : "relative h-[60vh] min-h-[420px]"} style={fullHeight ? {height:2000,minHeight:"100vh"} : undefined}
            data-testid="booking-partner-panel">
      {loading && (
        <div className="absolute inset-0 flex items-center
                                justify-center bg-stone-100">
          <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
        </div>
      )}
      <iframe src={url} title={`Booking via ${label}`}
                 onLoad={onLoad}
                 className="w-full h-full border-0 bg-white"
                 referrerPolicy="strict-origin-when-cross-origin"
                 sandbox="allow-same-origin allow-scripts
                              allow-forms allow-popups
                              allow-popups-to-escape-sandbox" />
      <a href={url} target="_blank" rel="noreferrer"
            onClick={onOpenExternal}
            data-testid="booking-partner-external-link"
            className="absolute bottom-3 right-3 text-xs
                          bg-white border border-stone-300 rounded-full
                          px-3 py-1.5 shadow hover:bg-stone-50
                          inline-flex items-center gap-1.5">
        Open {label} in new tab
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}
